import React, { useState } from 'react';
import { HelpCircle, X, ChevronRight, ChevronLeft, Lightbulb } from 'lucide-react';

interface GuideStep {
  title: string;
  description: string;
  tips?: string[];
}

const GUIDE_STEPS: GuideStep[] = [
  {
    title: '1. Select Platform & Account',
    description: 'Choose which social media platform you want to post to and select the specific account.',
    tips: [
      'You can only select one platform per task',
      'Make sure the account is connected and active',
      'Some platforms have different requirements for content',
    ],
  },
  {
    title: '2. Write Your Content',
    description: 'Create the text content for your post. You can use formatting options.',
    tips: [
      'Use **text** for bold formatting',
      'Use *text* for italic formatting',
      'Use __text__ for underline formatting',
      'Keep it concise and engaging',
    ],
  },
  {
    title: '3. Add Image (Optional)',
    description: 'Upload an image or use AI generation options.',
    tips: [
      'Generate Image: AI will create an image based on your content',
      'Modify Image: AI will enhance or modify your uploaded image',
      'Full Modify Image: Comprehensive AI modifications to your image',
      'Supported formats: JPG, PNG, GIF',
      'Max file size: 5MB',
    ],
  },
  {
    title: '4. AI-Generated Label (Instagram Only)',
    description: 'Mark your post as AI-generated if using AI tools to create content or images.',
    tips: [
      'Required by Meta for AI-generated content',
      'Instagram will show "Made with AI" label',
      'Only available for Instagram and Instagram Direct',
    ],
  },
  {
    title: '5. Set Schedule',
    description: 'Choose when your post should be published.',
    tips: [
      'Once: Post will be published one time only',
      'Daily: Post every day at the specified time',
      'Weekly: Post on selected days of the week',
      'Monthly: Post on the same date each month',
      'Timezone: Make sure to select your correct timezone',
    ],
  },
  {
    title: '6. Webhook (Advanced)',
    description: 'Optional webhook URL for post status notifications.',
    tips: [
      'Get real-time notifications when post is published',
      'Receive status updates and error notifications',
      'Useful for automation and integrations',
      'Leave empty to use default webhook',
    ],
  },
  {
    title: '7. Schedule or Test',
    description: 'Save your task or test it immediately.',
    tips: [
      'Schedule Task: Saves the task for future execution',
      'Test Now: Publishes immediately (only for existing tasks)',
      'Make sure all required fields are filled',
      'Check your credit balance before scheduling',
    ],
  },
];

export const TaskFormGuide: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < GUIDE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentGuide = GUIDE_STEPS[currentStep];

  return (
    <>
      {/* Help Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 z-40 transition-transform hover:scale-110"
        aria-label="Open guide"
      >
        <HelpCircle className="w-6 h-6" />
      </button>

      {/* Guide Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    How to Schedule Posts
                  </h2>
                </div>
                <p className="text-sm text-gray-600">
                  Step-by-step guide to creating scheduled posts
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Progress Indicators */}
            <div className="px-6 pt-4">
              <div className="flex gap-2">
                {GUIDE_STEPS.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStep(index)}
                    className={`flex-1 h-2 rounded-full transition-all ${
                      index === currentStep
                        ? 'bg-blue-600'
                        : index < currentStep
                        ? 'bg-blue-300'
                        : 'bg-gray-200'
                    }`}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>
              <div className="mt-2 text-center text-sm text-gray-600">
                Step {currentStep + 1} of {GUIDE_STEPS.length}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {currentGuide.title}
                  </h3>
                  <p className="text-gray-700">
                    {currentGuide.description}
                  </p>
                </div>

                {currentGuide.tips && currentGuide.tips.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Tips & Notes
                    </h4>
                    <ul className="space-y-2">
                      {currentGuide.tips.map((tip, index) => (
                        <li key={index} className="text-sm text-blue-800 flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t bg-gray-50 flex justify-between items-center gap-4">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg
                  disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <div className="flex-1 text-center">
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Close Guide
                </button>
              </div>

              <button
                onClick={handleNext}
                disabled={currentStep === GUIDE_STEPS.length - 1}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg
                  hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
